/** Helpers for turning user input into SIP targets and showing numbers nicely. */

const DIALABLE = /^[+]?[0-9*#]+$/;

/** Strip formatting characters people paste in: spaces, dashes, dots, brackets. */
export function cleanNumber(input: string): string {
  return input.trim().replace(/[\s\-().]/g, '');
}

/**
 * Build a SIP URI for a dial string.
 * - "sip:..." / "sips:..." is used verbatim
 * - "user@host" gets a sip: prefix
 * - anything else must be a dialable number and is placed on the account domain
 * Returns null when the input can't be dialled.
 */
export function toSipTarget(input: string, domain: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  if (/^sips?:/i.test(raw)) return raw;
  if (raw.includes('@')) return `sip:${raw}`;
  const num = cleanNumber(raw);
  if (!DIALABLE.test(num)) return null;
  return `sip:${num}@${domain}`;
}

/** True when a string is a plausible dial string (digits, *, #, leading +). */
export function isDialable(input: string): boolean {
  const raw = input.trim();
  if (raw.includes('@') || /^sips?:/i.test(raw)) return true;
  return DIALABLE.test(cleanNumber(raw));
}

/** Pretty-print Australian numbers; leave everything else alone. */
export function formatNumber(n: string): string {
  const d = n.replace(/\s/g, '');
  if (/^0[23578]\d{8}$/.test(d)) return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`; // 02 8287 3300
  if (/^04\d{8}$/.test(d)) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`; // 0452 098 607
  if (/^1[38]00\d{6}$/.test(d)) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  if (/^\+614\d{8}$/.test(d)) return `+61 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`; // +61 452 098 607
  if (/^\+61[2378]\d{8}$/.test(d)) return `+61 ${d.slice(3, 4)} ${d.slice(4, 8)} ${d.slice(8)}`; // +61 2 8287 3300
  return n;
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/**
 * Canonical form for storing and matching numbers (E.164 where possible). The trunk is Sydney-based,
 * so FusionPBX's Local_8_Digit route implies area 02: 8287 3300 → +61282873300.
 *   +61282873300 / 61282873300 / 0282873300 / 82873300 → +61282873300
 *   0011 44 20… → +4420…      13/1300/1800 numbers and extensions stay as-is (no E.164 form)
 */
export function toE164AU(n: string): string {
  const d = cleanNumber(n);
  if (/^\+\d+$/.test(d)) return d;
  if (/^0011\d{5,15}$/.test(d)) return `+${d.slice(4)}`;
  if (/^61[2-478]\d{8}$/.test(d)) return `+${d}`;
  if (/^0[2-478]\d{8}$/.test(d)) return `+61${d.slice(1)}`;
  if (/^[2-9]\d{7}$/.test(d)) return `+612${d}`;
  return d;
}

/**
 * What to send to the PBX for a stored number. Australian numbers go out as 10-digit national
 * (Local_10_Digit / Local_Service_Numbers routes — the most dependable), everything else unchanged.
 */
export function dialString(n: string): string {
  const e = toE164AU(n);
  if (/^\+61[2-478]\d{8}$/.test(e)) return `0${e.slice(3)}`;
  return e;
}

/** Same subscriber? (compares canonical forms) */
export function sameNumber(a: string, b: string): boolean {
  if (!a || !b) return false;
  return toE164AU(a) === toE164AU(b);
}

/** Human display: national AU format for AU numbers, spaced E.164 otherwise. */
export function displayNumber(n: string): string {
  const e = toE164AU(n);
  if (/^\+61[2-478]\d{8}$/.test(e)) return formatNumber(`0${e.slice(3)}`);
  return formatNumber(e);
}
